import { DOSE_UNITS, UNKNOWN_UNIT, isNamedForm } from "@/constants/medication-dosage";
import { UCUM_SYSTEM } from "@/utils/fhir-constants";

import { fhirR4 } from "@smile-cdr/fhirts";
import { MedicationStatement } from "@smile-cdr/fhirts/dist/FHIR-R4/classes/medicationStatement";

/**
 * Every MedicationStatement we write carries its medication as a contained
 * `Medication` referenced by `medicationReference` — one model for both
 * single-ingredient and combination products, per VMD-699. The product name is
 * `Medication.code.text`; each active strength is a `Medication.ingredient[]`
 * entry whose `strength` is a Ratio ("875 mg per 1 tablet").
 *
 * `medicationCodeableConcept` is no longer written. It is still read as a last
 * resort so records created before this change keep displaying their name.
 */

/**
 * Fixed id for the contained Medication, referenced as "#med-rx".
 *
 * Matches the VEG-535 migration (`MEDICATION_REFERENCE_MIGRATION.md`) and the
 * Engage shim. Aligning on one id is what lets the replace-by-id below find the
 * entry it means to replace — writing a different id would repoint the
 * reference and strand the migrated Medication in `contained` forever.
 */
export const CONTAINED_MEDICATION_ID = "med-rx";

/**
 * `Medication.form` as a human-readable CodeableConcept ("Tablet").
 *
 * Counted forms only. `Medication.form` is the product's dose form, and mL, L
 * and IU are amounts a dose is measured in rather than forms it comes in:
 * deriving it from those gives "ML" for every liquid, which is both wrong and
 * less than a real record already says ("Oral Suspension"). §10.1 asks for the
 * form the *name* implies, which is a different rule from the quantity's unit.
 *
 * Stores the option value ("tablet"), not the display label. Every other unit on
 * the resource is stored as its option value, the migrated records carry no
 * `Medication.form` to stay consistent with, and `formValueFromLabel` matches
 * both spellings so anything written before this still reads back.
 */
const formDisplay = (form: string): string => {
  const option = DOSE_UNITS.find(unit => unit.value === form);
  return option?.counted ? option.value : "";
};

/**
 * The inverse of `formDisplay`, for reading a form back off `Medication.form`.
 * Matches the singular label the write puts there, then the raw option value in
 * case a record stores one. Returns "" when nothing matches, which is the same
 * as no form.
 */
export const formValueFromLabel = (label?: string): string => {
  const text = label?.trim();
  if (!text) {
    return "";
  }
  const lower = text.toLowerCase();
  const option = DOSE_UNITS.find(
    unit => unit.value.toLowerCase() === lower || unit.label.replace(/\(s\)$/, "").toLowerCase() === lower
  );
  return option?.value ?? "";
};

/**
 * One active-ingredient strength row in the Add/Edit form.
 *
 * No ingredient name: VMD-713 removed the input, and every entry is now named
 * after the product on save. `stored` still carries whatever the record had.
 */
export interface IngredientRow {
  strength: string;
  unit: string;
  /**
   * The denominator unit this row was *read* with. No longer authored or
   * written: the Form picker is the denominator for every row, so this only
   * carries what the record said when it was opened, which is one of the inputs
   * the form derivation ranks (`medicationStatementToFormData`). A save ignores
   * it and writes the chosen Form as the resource's denominator, which is the
   * collapse onto one noun.
   */
  per: string;
  /**
   * How many of the form the strength is quoted against — the "5" in "250 mg per
   * 5 mL". Blank means one. Only meaningful for a form quoted per a volume; the
   * write ignores it otherwise, so switching away from mL cannot leave a stale
   * value behind in a hidden field.
   */
  perValue: string;
  /**
   * The stored `Medication.ingredient[]` entry this row came from, if any.
   * Carried so an entry the form cannot fully express — a coded ingredient with
   * no text and no strength, say — round-trips instead of being flattened away.
   * Not user-editable; removing the row is what deletes the entry.
   */
  stored?: fhirR4.MedicationIngredient;
}

export const emptyIngredientRow = (): IngredientRow => ({
  strength: "",
  unit: "",
  per: "",
  perValue: "",
});

/**
 * True when a row is worth persisting. A row backed by a stored entry always
 * counts, even when every visible field is blank — the form not being able to
 * show something is not a reason to delete it.
 */
export const ingredientRowHasContent = (row: IngredientRow): boolean => Boolean(row.strength.trim() || row.stored);

/** Per-row translation keys for strength input the save would not carry. */
export type IngredientRowErrors = Record<number, string>;

/**
 * Flags a strength with no unit. "875" on its own says nothing about what was
 * measured, and the unit list carries an explicit "unknown" for the case where
 * the curator genuinely does not know, so leaving it blank is never the answer.
 *
 * Returns translation keys, not sentences — the editor renders them.
 */
export const validateIngredientRows = (rows: IngredientRow[]): IngredientRowErrors => {
  const errors: IngredientRowErrors = {};
  rows.forEach((row, index) => {
    if (row.strength.trim() && !row.unit) {
      errors[index] = "pages.medications.dosage.errors.ingredientUnitRequired";
    }
  });
  return errors;
};

/** A product is a combination once it has more than one active ingredient. */
export const isCombinationProduct = (rows: IngredientRow[]): boolean => rows.filter(ingredientRowHasContent).length > 1;

export const buildStrengthQuantity = (value: number, unit: string): fhirR4.Quantity => {
  const def = DOSE_UNITS.find(u => u.value === unit);
  // Strict: a unit that is not one of ours has no UCUM code, and defaulting to
  // true fabricated one for a legacy 'tab' or a label-cased 'mEq'.
  const isUcum = def?.ucum === true;
  return {
    value,
    unit,
    ...(isUcum && { system: UCUM_SYSTEM, code: def?.ucumCode || unit }),
  };
};

/**
 * How many of the denominator unit the strength is quoted against. Blank, zero
 * and anything unparseable all mean one, which is what a strength is quoted per
 * unless the row says otherwise.
 */
export const strengthDenominatorValue = (raw: string): number => {
  const value = parseFloat(raw);
  return Number.isNaN(value) || value <= 0 ? 1 : value;
};

/**
 * Strength denominator as a Quantity — "875 mg per **1 tablet**", or "250 mg
 * per **5 mL**" for a liquid.
 *
 * Built on top of any stored denominator so unmodelled fields ride along, but
 * `system` and `code` are re-derived from the unit rather than spread through:
 * a UCUM code left over from a previous unit is worse than no code at all.
 */
export const buildStrengthDenominator = (value: number, unit: string, stored?: fhirR4.Quantity): fhirR4.Quantity => {
  const { system: _system, code: _code, unit: _unit, ...rest } = stored ?? {};
  // No form chosen: "10 mg per 1", which satisfies rat-1 without asserting a
  // dose form the source never stated. A numerator with no denominator is
  // invalid FHIR and HealthLake rejects it, so the Ratio always gets both.
  if (!unit) {
    return { ...rest, value };
  }
  return { ...rest, ...buildStrengthQuantity(value, unit) };
};

/**
 * Carries any coding (RxNorm and friends) from a stored CodeableConcept onto the
 * one we are about to write. The coding describes a specific product, so a
 * rename invalidates it — that case keeps the new text and drops the coding.
 *
 * `previousText` is the name the record previously *resolved* to, not the raw
 * stored `text`: a concept can carry codings with no text at all, and comparing
 * against the empty value discarded exactly the codings worth keeping.
 */
const preserveCoding = (
  text: string,
  previous?: fhirR4.CodeableConcept,
  previousText?: string
): fhirR4.CodeableConcept => {
  const unchanged = (previousText ?? previous?.text ?? "") === text;
  return previous?.coding?.length && unchanged ? { ...previous, text } : { text };
};

/**
 * Builds the contained `Medication` for a statement.
 *
 * `previous` is the Medication currently stored on the resource and
 * `previousName` the name that record resolved to. The form edits names and
 * strengths only, so codings and any ingredient detail it cannot express are
 * carried across rather than rebuilt away on every save.
 *
 * `form` is the dose form, chosen once for the medication. It lands on
 * `Medication.form` and is the denominator every strength is quoted against, so
 * a combination cannot end up with its parts quoted per different things.
 */
export const buildContainedMedication = (
  name: string,
  ingredients: IngredientRow[],
  form = "",
  previous?: fhirR4.Medication,
  previousName?: string
): fhirR4.Medication => {
  const medication: fhirR4.Medication = {
    resourceType: "Medication",
    id: CONTAINED_MEDICATION_ID,
    code: preserveCoding(name, previous?.code, previousName),
  };

  // "Unknown" is an admitted absence, not a dose form, so it names neither
  // Medication.form nor the strength denominator.
  const namedForm = isNamedForm(form) ? form : "";

  // A stored form whose text does not map to any option says more than we can
  // ("Oral Suspension", "Tablet, Film Coated"), so it is kept. One that does map
  // is only overwritten when the curator has moved to a different form.
  const storedForm = previous?.form;
  const storedMeans = formValueFromLabel(storedForm?.text);
  const formLabel = formDisplay(namedForm);
  if (storedForm && (!storedMeans || storedMeans === namedForm)) {
    medication.form = storedForm;
  } else if (formLabel) {
    // A coding describes the form it was written for, so a change of form
    // invalidates it the same way a rename invalidates an ingredient coding.
    medication.form =
      storedMeans && storedMeans !== namedForm ? { text: formLabel } : { ...storedForm, text: formLabel };
  }
  // Nothing else: a stored form the curator has moved away from is left off
  // rather than kept, since "Tablet" beside an mL quantity is a positive false
  // statement where an absent form is only silence.

  const rows = ingredients.filter(ingredientRowHasContent);
  if (rows.length > 0) {
    medication.ingredient = rows.map(row => {
      const stored = row.stored;
      // Start from the stored entry so anything unmodelled rides along, then
      // re-author only what the form owns.
      const ingredient: fhirR4.MedicationIngredient = {
        ...stored,
        // A stored isActive is the record's own assertion; only assume true for
        // a row the clinician just added.
        isActive: stored?.isActive ?? true,
      };

      // VMD-713: every ingredient is named after the product. There is no name
      // input any more, and a breakdown nobody on our side can produce reliably
      // is worse than none: when a curator does know one, they put it in the
      // medication name. Matches Engage (VEG-760).
      //
      // Except when the entry is identified by reference alone. `item[x]` is a
      // choice type, so writing the concept would delete the reference, and that
      // reference is the only thing saying what the ingredient is.
      if (!stored?.itemReference || stored.itemCodeableConcept) {
        // Compared against the product's previous name where we have it, not the
        // entry's own stored text. The item text tracks the product now, and a
        // stored ingredient text almost never equals the product name, so
        // comparing against that would strip an ingredient coding on every save.
        ingredient.itemCodeableConcept = preserveCoding(
          name,
          stored?.itemCodeableConcept,
          previousName ?? (stored?.itemCodeableConcept?.text || (stored ? name : undefined))
        );
      }

      const strength = parseFloat(row.strength);
      // Unconditional delete on the else: the ingredient spreads from `stored`,
      // so a conditional one left the old value on a row the curator cleared.
      if (row.strength && !Number.isNaN(strength)) {
        ingredient.strength = {
          ...stored?.strength,
          numerator: buildStrengthQuantity(strength, row.unit),
          denominator: buildStrengthDenominator(
            strengthDenominatorValue(row.perValue),
            isNamedForm(row.per) ? row.per : namedForm,
            stored?.strength?.denominator
          ),
        };
      } else {
        delete ingredient.strength;
      }

      return ingredient;
    });
  }

  return medication;
};

/**
 * The contained `Medication` a resource's `medicationReference` points at.
 *
 * Typed on the structural subset it reads (`contained` + `medicationReference`)
 * so it serves both `MedicationStatement` and `MedicationRequest`, which carry
 * the contained Medication the same way.
 */
export const getContainedMedication = (
  resource: Pick<fhirR4.MedicationStatement, "contained" | "medicationReference">
): fhirR4.Medication | undefined => {
  const contained = resource.contained as fhirR4.Medication[] | undefined;
  if (!contained?.length) {
    return undefined;
  }
  const medications = contained.filter(entry => entry?.resourceType === "Medication");
  if (!medications.length) {
    return undefined;
  }
  // Prefer the one the reference actually names; "#id" is a local reference.
  const reference = resource.medicationReference?.reference ?? "";
  if (reference.startsWith("#")) {
    const id = reference.slice(1);
    const matched = medications.find(entry => entry.id === id);
    if (matched) {
      return matched;
    }
  }
  return medications[0];
};

const trimmed = (value?: string): string | undefined => {
  const result = value?.trim();
  return result ? result : undefined;
};

/**
 * A CodeableConcept's human-readable name. Falls back to the first coding that
 * carries a display, because EMR-sourced concepts routinely arrive coded with
 * no `text` at all.
 */
const codeableConceptName = (concept?: fhirR4.CodeableConcept): string | undefined =>
  trimmed(concept?.text) ?? concept?.coding?.map(coding => trimmed(coding.display)).find(Boolean);

/**
 * Resolves the medication name for display.
 *
 * Contained first: the contained Medication is authoritative and
 * `medicationReference.display` is a denormalized copy of its `code.text`.
 * `medicationCodeableConcept` is last — it only appears on records the
 * medication[x] migration has not reached, and on responses the backend read
 * shim has already touched.
 *
 * Precedence matches Engage's `medication_name.ts` so the same record resolves
 * to the same name through either backend.
 */
export const resolveMedicationName = (resource: MedicationStatement): string =>
  codeableConceptName(getContainedMedication(resource)?.code) ??
  trimmed(resource.medicationReference?.display) ??
  codeableConceptName(resource.medicationCodeableConcept) ??
  "";

/** Hydrates the form's ingredient rows from a statement's contained Medication. */
export const medicationToIngredientRows = (resource: MedicationStatement): IngredientRow[] => {
  const ingredients = getContainedMedication(resource)?.ingredient ?? [];
  return ingredients.map(ingredient => {
    const denominator = ingredient.strength?.denominator;
    return {
      strength: ingredient.strength?.numerator?.value?.toString() ?? "",
      unit: ingredient.strength?.numerator?.unit ?? "",
      per: denominator?.unit ?? "",
      // A denominator of one is the norm and reads as noise in an optional
      // field, so it hydrates blank. Blank serializes back to one, so the
      // round-trip is unchanged.
      perValue: denominator?.value !== undefined && denominator.value !== 1 ? denominator.value.toString() : "",
      stored: ingredient,
    };
  });
};

/**
 * The shared "per 5 mL" suffix, when there is one.
 *
 * Only rendered when every ingredient quotes its strength against the same
 * denominator and that denominator is not a single unit: "per 1 tablet" is what
 * a strength means by default and adding it says nothing, while a combination
 * whose ingredients disagree cannot be summarized on one line at all.
 */
const sharedDenominator = (rows: IngredientRow[], form: string): string => {
  const values = new Set(rows.map(row => strengthDenominatorValue(row.perValue)));
  if (values.size !== 1) {
    return "";
  }
  const [only] = [...values];
  // Gated exactly as the write is: "per 1 tablet" is what a strength means by
  // default and saying it adds nothing, but any other quantity is real content
  // whatever the form. A legacy "per 2 tablet" is shown rather than silently
  // rounded to one.
  return isNamedForm(form) && only !== 1 ? `${only} ${form}` : "";
};

/** Connectives for the canonical rendering, passed in translated by UI callers. */
export interface StrengthLabels {
  /** Joins a strength to its denominator, e.g. "per" in "250 mg per 5 mL". */
  per?: string;
  /**
   * Stands in for the unknown unit. Spelled out rather than printed bare, so
   * "875 unknown" never reads as a unit called unknown.
   */
  unknownUnit?: string;
}

/**
 * Canonical strength rendering for a combination product: "875/125 mg" when the
 * ingredients share a unit, "875 mg/125 mcg" when they do not. A single
 * ingredient renders as plain "875 mg". A per-volume strength gains its
 * denominator, e.g. "250 mg per 5 mL".
 */
export const formatCombinationStrength = (rows: IngredientRow[], labels: StrengthLabels = {}, form = ""): string => {
  const { per: perLabel = "per", unknownUnit = "(unit unknown)" } = labels;
  const withStrength = rows.filter(row => row.strength.trim());
  if (!withStrength.length) {
    return "";
  }

  const unitLabel = (unit: string): string => (unit === UNKNOWN_UNIT ? unknownUnit : unit);

  const units = [...new Set(withStrength.map(row => row.unit))];
  let values: string;
  if (units.length === 1) {
    // One shared unit collapses to a single trailing label: "875/125 mg".
    const joined = withStrength.map(row => row.strength).join("/");
    values = units[0] ? `${joined} ${unitLabel(units[0])}` : joined;
  } else {
    values = withStrength.map(row => (row.unit ? `${row.strength} ${unitLabel(row.unit)}` : row.strength)).join("/");
  }

  const denominator = sharedDenominator(withStrength, form);
  return denominator ? `${values} ${perLabel} ${denominator}` : values;
};
